import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { IonIcon } from '@ionic/react';
import {
  close, navigateOutline, layersOutline,
  chevronForwardOutline, chevronBackOutline, eyeOutline,
  searchOutline, giftOutline, addOutline,
} from 'ionicons/icons';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { useMapFamilies, usePendingReview, useMapMembers } from '@/hooks/useMapFamilies';
import { useSectors, useSectorsGeoJson } from '@/hooks/useSectors';
import SectorPolygonsLayer from '@/components/map/SectorPolygonsLayer';
import type { MapFamily, MapMember, VisitStatus } from '@/api/maps';
import type { Sector } from '@/types/sector';
import { useHistory } from 'react-router-dom';
import { locationOutline } from 'ionicons/icons';
import VisitModal from '@/components/visits/VisitModal';
import NeedModal from '@/components/visits/NeedModal';
import SectorFamilyPanel from '@/components/map/SectorFamilyPanel';
import { VISIT_TYPE_LABELS, VISIT_TYPE_COLORS } from '@/utils/labels';
import type { VisitType } from '@/types/visit';
import { cartoTileUrl } from '@/utils/cartoTiles';

/* ══════════════════════════════════════════
   Constants
   ══════════════════════════════════════════ */

const ARAD_CENTER: [number, number] = [46.1866, 21.3123];

const STATUS_CONFIG: Record<VisitStatus, { color: string; label: string; description: string; badgeBg: string }> = {
  green:  { color: '#059669', label: 'Sub 6 luni',      description: 'sub 6 luni',       badgeBg: '#ecfdf5' },
  yellow: { color: '#d97706', label: '6–12 luni',       description: '6-12 luni',        badgeBg: '#fffbeb' },
  red:    { color: '#dc2626', label: 'Peste 12 luni',   description: 'peste 12 luni',    badgeBg: '#fef2f2' },
  gray:   { color: '#94a3b8', label: 'Fără vizită',     description: 'niciodată vizitat', badgeBg: '#f1f5f9' },
};

const STATUS_PRIORITY: Record<VisitStatus, number> = { gray: 0, green: 1, yellow: 2, red: 3 };

const TILE_URLS = {
  light: cartoTileUrl('rastertiles/voyager'),
  dark: cartoTileUrl('dark_all'),
};
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>';

/* ══════════════════════════════════════════
   Marker Factories
   ══════════════════════════════════════════ */

function createStatusIcon(status: VisitStatus, initials: string = ''): L.DivIcon {
  const { color } = STATUS_CONFIG[status];
  const safeInitials = (initials || '?').slice(0, 2);

  return L.divIcon({
    className: '',
    html: `<div class="mpin mpin--${status}" style="--pin-color: ${color}"><span class="mpin__initials">${safeInitials}</span></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -16],
  });
}

/* ── Member pin: violet circle with initials (distinct from family pins) ── */
function createMemberIcon(initials: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div class="mpin-member"><span>${initials || '?'}</span></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -16],
  });
}

function createMemberClusterIcon(cluster: L.MarkerCluster): L.DivIcon {
  const count = cluster.getChildCount();
  const size = count >= 50 ? 52 : count >= 10 ? 46 : 40;
  const fontSize = count >= 50 ? 14 : count >= 10 ? 13 : 12;
  return L.divIcon({
    className: '',
    html: `<div class="mcluster mcluster--members" style="width: ${size}px; height: ${size}px">
      <span class="mcluster__count" style="font-size: ${fontSize}px">${count}</span>
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/* ── Donut cluster: SVG ring showing status proportions ── */
function createDonutClusterIcon(cluster: L.MarkerCluster): L.DivIcon {
  const markers = cluster.getAllChildMarkers();
  const count = markers.length;
  const counts: Record<VisitStatus, number> = { green: 0, yellow: 0, red: 0, gray: 0 };

  let worstStatus: VisitStatus = 'gray';
  let worstPriority = 0;

  markers.forEach(m => {
    const status = ((m.options as Record<string, unknown>).status as VisitStatus) || 'gray';
    counts[status]++;
    const p = STATUS_PRIORITY[status];
    if (p > worstPriority) { worstPriority = p; worstStatus = status; }
  });

  // Build SVG donut arcs
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const segments: string[] = [];
  let offset = 0;
  const order: VisitStatus[] = ['red', 'yellow', 'green', 'gray'];

  order.forEach(status => {
    if (counts[status] === 0) return;
    const pct = counts[status] / count;
    const length = pct * circumference;
    segments.push(
      `<circle cx="22" cy="22" r="${radius}" fill="none" stroke="${STATUS_CONFIG[status].color}" stroke-width="5" stroke-dasharray="${length} ${circumference - length}" stroke-dashoffset="${-offset}" />`
    );
    offset += length;
  });

  const size = count >= 50 ? 52 : count >= 10 ? 46 : 40;
  const fontSize = count >= 50 ? 14 : count >= 10 ? 13 : 12;
  const { color: ringColor } = STATUS_CONFIG[worstStatus];

  return L.divIcon({
    className: '',
    html: `
      <div class="mcluster" style="--cluster-ring: ${ringColor}20; width: ${size}px; height: ${size}px">
        <svg viewBox="0 0 44 44" class="mcluster__donut">${segments.join('')}</svg>
        <span class="mcluster__count" style="font-size: ${fontSize}px">${count}</span>
      </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/* ══════════════════════════════════════════
   Popup HTML builder
   ══════════════════════════════════════════ */

function buildPopupHtml(family: MapFamily, pastoralOnly: boolean): string {
  const activeStatus = pastoralOnly ? (family.pastoral_visit_status || 'gray') : family.visit_status;
  const { color } = STATUS_CONFIG[activeStatus];
  const lastVisit = family.last_visit_date
    ? new Date(family.last_visit_date).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Niciodată';
  const lastPastoralVisit = family.last_pastoral_visit_date
    ? new Date(family.last_pastoral_visit_date).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Niciodată';
  const pastoralColor = STATUS_CONFIG[family.pastoral_visit_status || 'gray'].color;

  return `
    <div class="mpopup">
      <div class="mpopup__strip" style="background: ${color}"></div>
      <div class="mpopup__body">
        <div class="mpopup__name">${family.display_name ?? family.name}</div>
        ${family.address ? `<div class="mpopup__address"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>${[family.address, family.numar ? `Nr. ${family.numar}` : '', family.bloc ? `Bl. ${family.bloc}` : '', family.scara ? `Sc. ${family.scara}` : '', family.etaj ? `Et. ${family.etaj}` : '', family.apartament ? `Ap. ${family.apartament}` : '', family.city].filter(Boolean).join(', ')}</div>` : ''}
        <button type="button" class="mpopup__fix-loc" data-fix-loc="${family.id}" data-fix-kind="family" title="Corectează poziția pe hartă">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
          <span class="mpopup__fix-loc-label">Corectează poziția</span>
          <svg class="mpopup__fix-loc-chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </button>
        <div class="mpopup__rows">
          <div class="mpopup__row">
            <span class="mpopup__label">Ultima vizită</span>
            <span class="mpopup__status" style="--status-color: ${STATUS_CONFIG[family.visit_status].color}; background: ${STATUS_CONFIG[family.visit_status].badgeBg}; color: ${STATUS_CONFIG[family.visit_status].color}">
              <span class="mpopup__status-dot" style="background: ${STATUS_CONFIG[family.visit_status].color}"></span>
              ${lastVisit}
            </span>
          </div>
          ${family.last_visit_type ? `<div class="mpopup__row">
            <span class="mpopup__label">Tip vizită</span>
            <span class="mpopup__type-badge" style="--type-color: ${VISIT_TYPE_COLORS[family.last_visit_type as VisitType] ?? '#64748b'}">
              ${VISIT_TYPE_LABELS[family.last_visit_type as VisitType] ?? family.last_visit_type}
            </span>
          </div>` : ''}
          <div class="mpopup__row">
            <span class="mpopup__label">Vizită pastorală</span>
            <span class="mpopup__status" style="--status-color: ${pastoralColor}; background: ${STATUS_CONFIG[family.pastoral_visit_status || 'gray'].badgeBg}; color: ${pastoralColor}">
              <span class="mpopup__status-dot" style="background: ${pastoralColor}"></span>
              ${lastPastoralVisit}
            </span>
          </div>
        </div>
        <div class="mpopup__actions">
          <button class="mpopup__btn mpopup__btn--primary" data-add-visit="${family.id}">+ Vizită</button>
          <button class="mpopup__btn mpopup__btn--warn" data-add-need="${family.id}">+ Nevoie</button>
          <button class="mpopup__btn mpopup__btn--amber" data-add-ajutor="${family.id}">+ Ajutor</button>
          <button class="mpopup__btn mpopup__btn--ghost" data-family-id="${family.id}">Detalii</button>
        </div>
      </div>
    </div>`;
}

function buildMemberPopupHtml(member: MapMember): string {
  const fullAddress = [
    member.address,
    member.numar ? `Nr. ${member.numar}` : '',
    member.bloc ? `Bl. ${member.bloc}` : '',
    member.scara ? `Sc. ${member.scara}` : '',
    member.etaj ? `Et. ${member.etaj}` : '',
    member.apartament ? `Ap. ${member.apartament}` : '',
    member.city,
  ].filter(Boolean).join(', ');

  return `
    <div class="mpopup">
      <div class="mpopup__strip" style="background: linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)"></div>
      <div class="mpopup__body">
        <div class="mpopup__name">${member.full_name}</div>
        <div style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px 2px 6px; font-size: 9.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #7c3aed; background: rgba(139,92,246,0.1); border: 1px solid rgba(139,92,246,0.25); border-radius: 999px; line-height: 1; margin: 4px 0 8px;">
          <span style="width: 5px; height: 5px; border-radius: 50%; background: #8b5cf6; box-shadow: 0 0 0 2px rgba(139,92,246,0.2)"></span>
          persoană individuală
        </div>
        ${fullAddress ? `<div class="mpopup__address"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>${fullAddress}</div>` : ''}
        <button type="button" class="mpopup__fix-loc mpopup__fix-loc--member" data-fix-loc="${member.id}" data-fix-kind="member" title="Corectează poziția pe hartă">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
          <span class="mpopup__fix-loc-label">Corectează poziția</span>
          <svg class="mpopup__fix-loc-chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </button>
        ${member.phone ? `<div class="mpopup__row"><span class="mpopup__label">Telefon</span><a href="tel:${member.phone}" style="color: #5e81f4; font-weight: 600; text-decoration: none">${member.phone}</a></div>` : ''}
        <div class="mpopup__actions">
          <button class="mpopup__btn mpopup__btn--ghost" data-member-id="${member.id}">Deschide profil</button>
        </div>
      </div>
    </div>`;
}

/* ══════════════════════════════════════════
   ClusterLayer (vanilla Leaflet in react-leaflet)
   ══════════════════════════════════════════ */

interface ClusterLayerProps {
  families: MapFamily[];
  pastoralOnly: boolean;
  onFamilyClick: (id: string) => void;
  onAddVisit?: (familyId: string) => void;
  onAddNeed?: (familyId: string) => void;
  onAddAjutor?: (familyId: string) => void;
  onFixLocation?: (kind: 'family' | 'member', id: string) => void;
}

const ClusterLayer: React.FC<ClusterLayerProps> = ({ families, pastoralOnly, onFamilyClick, onAddVisit, onAddNeed, onAddAjutor, onFixLocation }) => {
  const map = useMap();

  useEffect(() => {
    const clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: createDonutClusterIcon,
      animate: true,
    });

    families.forEach((family) => {
      if (!family.latitude || !family.longitude) return;

      const status = pastoralOnly
        ? (family.pastoral_visit_status || 'gray')
        : (family.visit_status || 'gray');
      const fallbackInitials = (family.name ?? family.display_name ?? '').slice(0, 1).toUpperCase();
      const marker = L.marker([family.latitude, family.longitude], {
        icon: createStatusIcon(status, family.initials || fallbackInitials),
        status, // attach for cluster reading
      } as L.MarkerOptions);

      marker.bindPopup(buildPopupHtml(family, pastoralOnly), {
        closeButton: true,
        maxWidth: 320,
        minWidth: 280,
        autoPan: true,
        autoPanPadding: [24, 24],
        keepInView: true,
        className: 'mpopup-wrapper',
      });

      const tooltipName = family.display_name ?? family.name;
      if (tooltipName) {
        marker.bindTooltip(tooltipName, {
          direction: 'top',
          offset: [0, -14],
          opacity: 1,
          className: `mpin-tooltip mpin-tooltip--${status}`,
        });
      }

      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);

    // Handle popup button clicks
    const handlePopupClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const fixLocBtn = target.closest('[data-fix-loc][data-fix-kind="family"]') as HTMLElement | null;
      if (fixLocBtn) { onFixLocation?.('family', fixLocBtn.dataset.fixLoc!); return; }
      const visitBtn = target.closest('[data-add-visit]') as HTMLElement | null;
      if (visitBtn) { onAddVisit?.(visitBtn.dataset.addVisit!); return; }
      const needBtn = target.closest('[data-add-need]') as HTMLElement | null;
      if (needBtn) { onAddNeed?.(needBtn.dataset.addNeed!); return; }
      const ajutorBtn = target.closest('[data-add-ajutor]') as HTMLElement | null;
      if (ajutorBtn) { onAddAjutor?.(ajutorBtn.dataset.addAjutor!); return; }
      const detailBtn = target.closest('[data-family-id]') as HTMLElement | null;
      if (detailBtn) onFamilyClick(detailBtn.dataset.familyId!);
    };
    map.getContainer().addEventListener('click', handlePopupClick);

    // Only fitBounds when markers are within a reasonable area (< ~50km spread).
    // When all families are shown (no filter), outliers far from Arad make fitBounds
    // zoom out so much that sector polygons become invisible.
    if (families.some(f => f.latitude && f.longitude)) {
      const bounds = clusterGroup.getBounds();
      if (bounds.isValid()) {
        const latSpan = bounds.getNorth() - bounds.getSouth();
        const lngSpan = bounds.getEast() - bounds.getWest();
        if (latSpan < 0.5 && lngSpan < 0.5) {
          map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
        }
      }
    }

    return () => {
      map.removeLayer(clusterGroup);
      map.getContainer().removeEventListener('click', handlePopupClick);
    };
  }, [map, families, pastoralOnly, onFamilyClick, onAddVisit, onAddNeed, onAddAjutor, onFixLocation]);

  return null;
};

/* ══════════════════════════════════════════
   MemberClusterLayer — orphan members with personal address
   ══════════════════════════════════════════ */

interface MemberClusterLayerProps {
  members: MapMember[];
  onMemberClick: (id: string) => void;
  onFixLocation?: (kind: 'family' | 'member', id: string) => void;
}

const MemberClusterLayer: React.FC<MemberClusterLayerProps> = ({ members, onMemberClick, onFixLocation }) => {
  const map = useMap();

  useEffect(() => {
    const clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: createMemberClusterIcon,
      animate: true,
    });

    members.forEach((member) => {
      if (!member.latitude || !member.longitude) return;

      const marker = L.marker([member.latitude, member.longitude], {
        icon: createMemberIcon(member.initials),
      });

      marker.bindPopup(buildMemberPopupHtml(member), {
        closeButton: true,
        maxWidth: 320,
        minWidth: 280,
        autoPan: true,
        autoPanPadding: [24, 24],
        keepInView: true,
        className: 'mpopup-wrapper',
      });

      if (member.full_name) {
        marker.bindTooltip(member.full_name, {
          direction: 'top',
          offset: [0, -14],
          opacity: 1,
          className: 'mpin-tooltip mpin-tooltip--member',
        });
      }

      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);

    const handlePopupClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const fixLocBtn = target.closest('[data-fix-loc][data-fix-kind="member"]') as HTMLElement | null;
      if (fixLocBtn) { onFixLocation?.('member', fixLocBtn.dataset.fixLoc!); return; }
      const memberBtn = target.closest('[data-member-id]') as HTMLElement | null;
      if (memberBtn) onMemberClick(memberBtn.dataset.memberId!);
    };
    map.getContainer().addEventListener('click', handlePopupClick);

    return () => {
      map.removeLayer(clusterGroup);
      map.getContainer().removeEventListener('click', handlePopupClick);
    };
  }, [map, members, onMemberClick, onFixLocation]);

  return null;
};

/* ══════════════════════════════════════════
   FlyToHandler — receives a target and flies + opens popup
   ══════════════════════════════════════════ */

const FlyToHandler: React.FC<{
  target: MapFamily | null;
  onDone: () => void;
}> = ({ target, onDone }) => {
  const map = useMap();

  useEffect(() => {
    if (!target || !target.latitude || !target.longitude) return;

    map.flyTo([target.latitude, target.longitude], 17, { duration: 0.8 });

    const timer = setTimeout(() => {
      // Find the marker at this location and open its popup
      map.eachLayer((layer) => {
        if (layer instanceof L.MarkerClusterGroup) {
          layer.eachLayer((marker) => {
            if (marker instanceof L.Marker) {
              const ll = marker.getLatLng();
              if (
                Math.abs(ll.lat - target.latitude) < 0.00001 &&
                Math.abs(ll.lng - target.longitude) < 0.00001
              ) {
                // Spiderfy the cluster if needed, then open popup
                const parent = layer.getVisibleParent(marker);
                if (parent && parent !== marker) {
                  layer.zoomToShowLayer(marker, () => marker.openPopup());
                } else {
                  marker.openPopup();
                }
              }
            }
          });
        }
      });
      onDone();
    }, 900);

    return () => clearTimeout(timer);
  }, [target, map, onDone]);

  return null;
};

/* ── FlyToMemberHandler — fly to an orphan member and open its popup ── */
const FlyToMemberHandler: React.FC<{
  target: MapMember | null;
  onDone: () => void;
}> = ({ target, onDone }) => {
  const map = useMap();

  useEffect(() => {
    if (!target || !target.latitude || !target.longitude) return;

    map.flyTo([target.latitude, target.longitude], 17, { duration: 0.8 });

    const timer = setTimeout(() => {
      map.eachLayer((layer) => {
        if (layer instanceof L.MarkerClusterGroup) {
          layer.eachLayer((marker) => {
            if (marker instanceof L.Marker) {
              const ll = marker.getLatLng();
              if (
                Math.abs(ll.lat - target.latitude) < 0.00001 &&
                Math.abs(ll.lng - target.longitude) < 0.00001
              ) {
                const parent = layer.getVisibleParent(marker);
                if (parent && parent !== marker) {
                  layer.zoomToShowLayer(marker, () => marker.openPopup());
                } else {
                  marker.openPopup();
                }
              }
            }
          });
        }
      });
      onDone();
    }, 900);

    return () => clearTimeout(timer);
  }, [target, map, onDone]);

  return null;
};

/* ── Fit map to a sector's bounds on initial load ── */
const FitToSector: React.FC<{
  geojson: GeoJSON.FeatureCollection | undefined;
  sectorId: string;
}> = ({ geojson, sectorId }) => {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (fitted.current || !geojson) return;
    const feature = geojson.features.find(f => f.properties?.id === sectorId);
    if (!feature) return;
    const layer = L.geoJSON(feature as GeoJSON.Feature);
    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 });
      fitted.current = true;
    }
  }, [map, geojson, sectorId]);

  return null;
};

/* ══════════════════════════════════════════
   MapPage
   ══════════════════════════════════════════ */

const MapPage: React.FC = () => {
  const history = useHistory();
  const urlParams = new URLSearchParams(history.location.search);
  const urlSectorId = urlParams.get('sector_id');
  const urlFlyId = urlParams.get('fly');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedSectors, setSelectedSectors] = useState<Set<string>>(
    urlSectorId ? new Set([urlSectorId]) : new Set()
  );
  const [selectedStatuses, setSelectedStatuses] = useState<Set<VisitStatus>>(new Set());
  const sidebarRef = useRef<HTMLDivElement>(null);

  const [showBoundaries, setShowBoundaries] = useState(true);
  const [familySearch, setFamilySearch] = useState('');
  const [flyTarget, setFlyTarget] = useState<MapFamily | null>(null);
  const [flyMemberTarget, setFlyMemberTarget] = useState<MapMember | null>(null);
  const [mobSheetOpen, setMobSheetOpen] = useState(false);
  const [ajutoareFilter, setAjutoareFilter] = useState<'received' | 'not_received' | null>(null);
  const [ajutoareMonths, setAjutoareMonths] = useState<3 | 6 | 12>(6);
  const [pastoralOnly, setPastoralOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<'families' | 'members' | 'both'>('both');

  const mapFilters = useMemo(() => {
    const f: Record<string, unknown> = {};
    if (urlSectorId) f.sector_id = urlSectorId;
    if (ajutoareFilter) {
      f.ajutoare_filter = ajutoareFilter;
      f.ajutoare_months = ajutoareMonths;
    }
    return Object.keys(f).length > 0 ? f : undefined;
  }, [urlSectorId, ajutoareFilter, ajutoareMonths]);

  const { data: mapFamilies, isLoading, refetch: refetchMap } = useMapFamilies(
    mapFilters as Record<string, string | number> | undefined
  );
  const { data: mapMembersData } = useMapMembers(
    mapFilters as Record<string, string | number> | undefined
  );
  const { data: sectorsData } = useSectors();
  const { data: pendingData } = usePendingReview(urlSectorId ?? undefined);
  const { data: sectorsGeoJson } = useSectorsGeoJson();
  const pendingCount = pendingData?.length ?? 0;

  const allFamilies: MapFamily[] = mapFamilies ?? [];
  const allMembers: MapMember[] = mapMembersData ?? [];
  const sectors: Sector[] = sectorsData ?? [];

  // Fly to a specific family from URL param
  const flyHandled = useRef(false);
  useEffect(() => {
    if (flyHandled.current || !urlFlyId || allFamilies.length === 0) return;
    const target = allFamilies.find(f => f.id === urlFlyId);
    if (target) {
      setFlyTarget(target);
      flyHandled.current = true;
    }
  }, [urlFlyId, allFamilies]);

  // Apply client-side filters
  const filteredFamilies = useMemo(() => {
    if (sourceFilter === 'members') return [];
    let result = allFamilies;
    if (selectedSectors.size > 0) {
      result = result.filter(f => f.sector_id && selectedSectors.has(f.sector_id));
    }
    if (selectedStatuses.size > 0) {
      result = result.filter(f => selectedStatuses.has(f.visit_status));
    }
    return result;
  }, [allFamilies, selectedSectors, selectedStatuses, sourceFilter]);

  const filteredMembers = useMemo(() => {
    if (sourceFilter === 'families') return [];
    let result = allMembers;
    if (selectedSectors.size > 0) {
      result = result.filter(m => m.sector_id && selectedSectors.has(m.sector_id));
    }
    return result;
  }, [allMembers, selectedSectors, sourceFilter]);

  // Stats
  const stats = useMemo(() => {
    const statusCounts: Record<VisitStatus, number> = { green: 0, yellow: 0, red: 0, gray: 0 };
    allFamilies.forEach(f => {
      const s = pastoralOnly ? (f.pastoral_visit_status || 'gray') : f.visit_status;
      statusCounts[s]++;
    });
    return {
      total: allFamilies.length,
      shown: filteredFamilies.length,
      ...statusCounts,
    };
  }, [allFamilies, filteredFamilies, pastoralOnly]);

  // Search context: filtered families when sectors selected, all otherwise
  const searchPool = selectedSectors.size > 0 ? filteredFamilies : allFamilies;
  const searchContextLabel = useMemo(() => {
    if (selectedSectors.size === 0) return null;
    const names = sectors
      .filter(s => selectedSectors.has(s.id))
      .map(s => `Sector ${s.number}`);
    return names.join(', ');
  }, [selectedSectors, sectors]);

  // Family search results (max 20 to keep sidebar snappy)
  const familySearchResults = useMemo(() => {
    const q = familySearch.trim().toLowerCase();
    if (q.length < 2) return [];
    return searchPool
      .filter(f => {
        const name = (f.display_name ?? f.name).toLowerCase();
        const addr = (f.address ?? '').toLowerCase();
        return name.includes(q) || addr.includes(q);
      })
      .slice(0, 20);
  }, [searchPool, familySearch]);

  // Orphan member search results
  const memberSearchResults = useMemo(() => {
    const q = familySearch.trim().toLowerCase();
    if (q.length < 2) return [];
    const pool = selectedSectors.size > 0 ? filteredMembers : allMembers;
    return pool
      .filter(m => {
        const name = m.full_name.toLowerCase();
        const addr = (m.address ?? '').toLowerCase();
        return name.includes(q) || addr.includes(q);
      })
      .slice(0, 10);
  }, [allMembers, filteredMembers, selectedSectors, familySearch]);

  const activeFilterCount = selectedSectors.size + selectedStatuses.size + (ajutoareFilter ? 1 : 0);

  // Show sector family panel when exactly 1 sector is selected
  const showFamilyPanel = selectedSectors.size === 1;
  const selectedSectorId = showFamilyPanel ? [...selectedSectors][0] : null;
  const selectedSectorObj = selectedSectorId ? sectors.find(s => s.id === selectedSectorId) : null;
  const sectorFamilies = useMemo(() => {
    if (!showFamilyPanel || !selectedSectorId) return [];
    return filteredFamilies;
  }, [showFamilyPanel, selectedSectorId, filteredFamilies]);

  const toggleSector = useCallback((id: string) => {
    setSelectedSectors(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      // Auto-expand mobile sheet when selecting a sector
      if (next.size === 1) setMobSheetOpen(true);
      return next;
    });
  }, []);

  const toggleStatus = useCallback((status: VisitStatus) => {
    setSelectedStatuses(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status); else next.add(status);
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedSectors(new Set());
    setSelectedStatuses(new Set());
    setAjutoareFilter(null);
  }, []);

  const handleFamilyClick = useCallback((id: string) => {
    history.push(`/families/${id}`);
  }, [history]);

  const handleMemberClick = useCallback((id: string) => {
    history.push(`/members/${id}`);
  }, [history]);

  /* ── Visit/Need from map ── */
  const [visitFamilyId, setVisitFamilyId] = useState<string | null>(null);
  const [needFamilyId, setNeedFamilyId] = useState<string | null>(null);

  const handleAddVisitFromMap = useCallback((familyId: string) => {
    setVisitFamilyId(familyId);
  }, []);
  const handleAddNeedFromMap = useCallback((familyId: string) => {
    setNeedFamilyId(familyId);
  }, []);
  const [ajutorFamilyId, setAjutorFamilyId] = useState<string | null>(null);
  const handleAddAjutorFromMap = useCallback((familyId: string) => {
    setAjutorFamilyId(familyId);
  }, []);

  const handleFixLocation = useCallback((kind: 'family' | 'member', id: string) => {
    const param = kind === 'member' ? 'member_id' : 'family_id';
    history.push(`/maps/review?${param}=${id}`);
  }, [history]);

  return (
    <div className="mpage">
      {/* ── Map ── */}
      <div className={`mpage__map${showFamilyPanel ? ' mpage__map--with-panel' : ''}`}>
        {isLoading ? (
          <div className="mpage__loading">
            <div className="mpage__spinner" />
            <span>Se încarcă harta...</span>
          </div>
        ) : (
          <MapContainer
            center={ARAD_CENTER}
            zoom={13}
            style={{ width: '100%', height: '100%' }}
            scrollWheelZoom
            zoomControl={false}
          >
            <TileLayer
              attribution={TILE_ATTR}
              url={TILE_URLS.light}
              subdomains="abcd"
              maxZoom={20}
            />
            {showBoundaries && (
              <SectorPolygonsLayer
                geojson={sectorsGeoJson}
                selectedSectorIds={selectedSectors}
                onSectorClick={toggleSector}
                showLabels
              />
            )}
            <ClusterLayer families={filteredFamilies} pastoralOnly={pastoralOnly} onFamilyClick={handleFamilyClick} onAddVisit={handleAddVisitFromMap} onAddNeed={handleAddNeedFromMap} onAddAjutor={handleAddAjutorFromMap} onFixLocation={handleFixLocation} />
            <MemberClusterLayer members={filteredMembers} onMemberClick={handleMemberClick} onFixLocation={handleFixLocation} />
            <FlyToHandler target={flyTarget} onDone={() => setFlyTarget(null)} />
            <FlyToMemberHandler target={flyMemberTarget} onDone={() => setFlyMemberTarget(null)} />
            {urlSectorId && <FitToSector geojson={sectorsGeoJson} sectorId={urlSectorId} />}
          </MapContainer>
        )}
        {pastoralOnly ? (
          <div className="mpage__pastoral-banner">
            <IonIcon icon={eyeOutline} />
            <span>Mod pastoral — culorile arată doar vizitele pastorale</span>
            <button onClick={() => setPastoralOnly(false)}>
              <IonIcon icon={close} />
            </button>
          </div>
        ) : null}
      </div>

      {/* ── Sidebar toggle ── */}
      <button
        className={`mpage__sidebar-toggle${sidebarOpen ? ' mpage__sidebar-toggle--open' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        title={sidebarOpen ? 'Ascunde panoul' : 'Arată panoul'}
      >
        <IonIcon icon={sidebarOpen ? chevronBackOutline : chevronForwardOutline} />
        {!sidebarOpen && activeFilterCount > 0 && (
          <span className="mpage__toggle-badge">{activeFilterCount}</span>
        )}
      </button>

      {/* ── Glassmorphic Sidebar ── */}
      <aside
        ref={sidebarRef}
        className={`mpage__sidebar${sidebarOpen ? ' mpage__sidebar--open' : ''}`}
      >
        {/* Header */}
        <div className="mside__header">
          <div className="mside__header-top">
            <IonIcon icon={navigateOutline} className="mside__header-icon" />
            <h2 className="mside__title">Harta Familiilor</h2>
          </div>
          <span className="mside__count">
            {stats.shown} din {stats.total} familii
            {sourceFilter !== 'families' && allMembers.length > 0 && (
              <> · {filteredMembers.length} din {allMembers.length} pers.</>
            )}
          </span>
        </div>

        {/* Source filter: Families / Members / Both */}
        <div className="mside__section">
          <div className="mside__section-title">
            <IonIcon icon={layersOutline} style={{ fontSize: 14 }} />
            Surse pe hartă
          </div>
          <div className="mside__chips">
            {([
              ['both', 'Ambele'],
              ['families', 'Familii'],
              ['members', 'Persoane individuale'],
            ] as const).map(([key, label]) => {
              const isActive = sourceFilter === key;
              const activeColor = key === 'members' ? '#8b5cf6' : '#5e81f4';
              return (
                <button
                  key={key}
                  className={`mside__chip${isActive ? ' mside__chip--active' : ''}`}
                  onClick={() => setSourceFilter(key)}
                  style={isActive ? { background: activeColor, borderColor: activeColor } : undefined}
                >
                  {!isActive && key === 'members' && (
                    <span className="mside__chip-dot" style={{ background: '#8b5cf6' }} />
                  )}
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Visit Status Legend / Filter */}
        <div className="mside__section">
          <div className="mside__section-title">
            <IonIcon icon={eyeOutline} style={{ fontSize: 14 }} />
            {pastoralOnly ? 'Vizită pastorală' : 'Ultima vizită'}
          </div>
          <div className="mside__statuses">
            {(Object.keys(STATUS_CONFIG) as VisitStatus[]).map(status => {
              const { color, label, description } = STATUS_CONFIG[status];
              const count = stats[status];
              const isActive = selectedStatuses.has(status);
              return (
                <button
                  key={status}
                  className={`mside__status-row${isActive ? ' mside__status-row--active' : ''}`}
                  onClick={() => toggleStatus(status)}
                >
                  <span className="mside__status-dot" style={{ background: color, boxShadow: `0 0 0 3px ${color}25` }} />
                  <span className="mside__status-label">{label}</span>
                  <span className="mside__status-desc">{description}</span>
                  <span className="mside__status-count">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Sector Filter */}
        <div className="mside__section">
          <div className="mside__section-title">
            <IonIcon icon={layersOutline} style={{ fontSize: 14 }} />
            Sectoare
          </div>
          <div className="mside__chips">
            {sectors.map(sector => {
              const isActive = selectedSectors.has(sector.id);
              return (
                <button
                  key={sector.id}
                  className={`mside__chip${isActive ? ' mside__chip--active' : ''}`}
                  onClick={() => toggleSector(sector.id)}
                  style={isActive ? { background: sector.color || '#5e81f4', borderColor: sector.color || '#5e81f4' } : undefined}
                >
                  {!isActive && sector.color && (
                    <span className="mside__chip-dot" style={{ background: sector.color }} />
                  )}
                  {sector.name}
                  {isActive && <IonIcon icon={close} style={{ fontSize: 12 }} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sector boundaries toggle */}
        <div className="mside__section">
          <button
            className={`mside__toggle-btn${showBoundaries ? ' mside__toggle-btn--active' : ''}`}
            onClick={() => setShowBoundaries(!showBoundaries)}
          >
            <IonIcon icon={layersOutline} style={{ fontSize: 15 }} />
            <span>Limite sectoare</span>
            <span className={`mside__toggle-indicator${showBoundaries ? ' mside__toggle-indicator--on' : ''}`} />
          </button>
        </div>

        {/* Pastoral-only filter toggle */}
        <div className="mside__section">
          <button
            className={`mside__toggle-btn${pastoralOnly ? ' mside__toggle-btn--active' : ''}`}
            onClick={() => setPastoralOnly(!pastoralOnly)}
          >
            <IonIcon icon={eyeOutline} style={{ fontSize: 15 }} />
            <span>Doar vizite pastorale</span>
            <span className={`mside__toggle-indicator${pastoralOnly ? ' mside__toggle-indicator--on' : ''}`} />
          </button>
          {pastoralOnly ? (
            <div style={{ fontSize: 11, color: '#5e81f4', padding: '4px 8px 0', lineHeight: 1.3 }}>
              Culorile reflectă doar vizitele pastorale (fără ajutoare)
            </div>
          ) : null}
        </div>

        {/* Ajutoare filter */}
        <div className="mside__section">
          <div className="mside__section-title">
            <IonIcon icon={giftOutline} style={{ fontSize: 14 }} />
            Ajutoare
          </div>
          <div className="mside__chips" style={{ marginBottom: ajutoareFilter ? 8 : 0 }}>
            {([['received', 'Au primit'], ['not_received', 'Nu au primit']] as const).map(([key, label]) => {
              const isActive = ajutoareFilter === key;
              return (
                <button
                  key={key}
                  className={`mside__chip${isActive ? ' mside__chip--active' : ''}`}
                  onClick={() => setAjutoareFilter(isActive ? null : key)}
                  style={isActive ? { background: '#d97706', borderColor: '#d97706' } : undefined}
                >
                  {label}
                  {isActive && <IonIcon icon={close} style={{ fontSize: 12 }} />}
                </button>
              );
            })}
          </div>
          {ajutoareFilter && (
            <div className="mside__chips">
              {([3, 6, 12] as const).map(months => {
                const isActive = ajutoareMonths === months;
                return (
                  <button
                    key={months}
                    className={`mside__chip${isActive ? ' mside__chip--active' : ''}`}
                    onClick={() => setAjutoareMonths(months)}
                    style={isActive ? { background: '#92400e', borderColor: '#92400e' } : undefined}
                  >
                    {months} luni
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Search & fly-to (families + orphan members) */}
        <div className="mside__section">
          <div className="mside__section-title">
            <IonIcon icon={searchOutline} style={{ fontSize: 14 }} />
            Caută pe hartă
          </div>
          {searchContextLabel && (
            <div style={{ fontSize: 11, color: '#5e81f4', fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#5e81f4', flexShrink: 0 }} />
              În {searchContextLabel}
              <button
                onClick={clearAllFilters}
                style={{ marginLeft: 'auto', fontSize: 10, color: '#8181a5', background: '#f5f5fa', border: 'none', borderRadius: 4, padding: '1px 6px', cursor: 'pointer' }}
              >
                Caută global
              </button>
            </div>
          )}
          <div className="mside__search-wrap">
            <IonIcon icon={searchOutline} className="mside__search-icon" />
            <input
              type="text"
              className="mside__search-input"
              placeholder={searchContextLabel ? `Caută în ${searchContextLabel}...` : 'Nume sau adresă...'}
              value={familySearch}
              onChange={e => setFamilySearch(e.target.value)}
            />
            {familySearch && (
              <button className="mside__search-clear" onClick={() => setFamilySearch('')}>
                <IonIcon icon={close} />
              </button>
            )}
          </div>
          {familySearch.trim().length >= 2 && (
            <div className="mside__search-results">
              {familySearchResults.length === 0 && memberSearchResults.length === 0 ? (
                <div className="mside__search-empty">Niciun rezultat</div>
              ) : (
                <>
                  {sourceFilter !== 'members' && familySearchResults.map(family => (
                    <button
                      key={`f-${family.id}`}
                      className="mside__search-item"
                      onClick={() => {
                        if (family.latitude && family.longitude) {
                          setFlyTarget(family);
                          setFamilySearch('');
                        }
                      }}
                      disabled={!family.latitude || !family.longitude}
                    >
                      <span
                        className="mside__search-dot"
                        style={{ background: STATUS_CONFIG[family.visit_status].color }}
                      />
                      <div className="mside__search-info">
                        <span className="mside__search-name">
                          {family.display_name ?? family.name}
                        </span>
                        {family.address && (
                          <span className="mside__search-addr">{family.address}</span>
                        )}
                      </div>
                      {family.latitude && family.longitude ? (
                        <IonIcon icon={navigateOutline} className="mside__search-fly" />
                      ) : (
                        <span className="mside__search-noloc">Fără loc.</span>
                      )}
                    </button>
                  ))}
                  {sourceFilter !== 'families' && memberSearchResults.length > 0 && (
                    <>
                      {familySearchResults.length > 0 && (
                        <div style={{ padding: '6px 10px 4px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8b5cf6' }}>
                          Persoane individuale
                        </div>
                      )}
                      {memberSearchResults.map(member => (
                        <button
                          key={`m-${member.id}`}
                          className="mside__search-item"
                          onClick={() => {
                            if (member.latitude && member.longitude) {
                              setFlyMemberTarget(member);
                              setFamilySearch('');
                            }
                          }}
                        >
                          <span
                            className="mside__search-dot"
                            style={{ background: '#8b5cf6', boxShadow: '0 0 0 2px rgba(139,92,246,0.2)' }}
                          />
                          <div className="mside__search-info">
                            <span className="mside__search-name">{member.full_name}</span>
                            {member.address && (
                              <span className="mside__search-addr">{member.address}</span>
                            )}
                          </div>
                          <IonIcon icon={navigateOutline} className="mside__search-fly" />
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Clear filters */}
        {activeFilterCount > 0 && (
          <div className="mside__clear-wrap">
            <button className="mside__clear-btn" onClick={clearAllFilters}>
              Șterge toate filtrele ({activeFilterCount})
            </button>
          </div>
        )}
      </aside>

      {/* ── Mobile bottom bar + expandable sheet ── */}
      <div className={`mpage__mob-bar${mobSheetOpen ? ' mpage__mob-bar--expanded' : ''}`}>
        {/* Collapsed: compact summary bar */}
        <button className="mpage__mob-peek" onClick={() => setMobSheetOpen(!mobSheetOpen)}>
          <div className="mpage__mob-peek-left">
            {showFamilyPanel && selectedSectorObj ? (
              <>
                <span className="mpage__mob-peek-count">{sectorFamilies.length}</span>
                <span className="mpage__mob-peek-label">familii · Sector {selectedSectorObj.number}</span>
              </>
            ) : (
              <>
                <span className="mpage__mob-peek-count">{stats.shown}</span>
                <span className="mpage__mob-peek-label">familii</span>
                {activeFilterCount > 0 && (
                  <span className="mpage__mob-peek-filters">
                    · {activeFilterCount} filtr{activeFilterCount === 1 ? 'u' : 'e'}
                  </span>
                )}
              </>
            )}
          </div>
          <div className="mpage__mob-peek-dots">
            {(Object.keys(STATUS_CONFIG) as VisitStatus[]).map(s => (
              <span
                key={s}
                className={`mpage__mob-peek-dot${selectedStatuses.has(s) ? ' mpage__mob-peek-dot--active' : ''}`}
                style={{ background: STATUS_CONFIG[s].color }}
              />
            ))}
          </div>
          <span className="mpage__mob-chevron">
            <IonIcon icon={mobSheetOpen ? close : chevronForwardOutline} />
          </span>
        </button>

        {/* Expanded: filters + search OR family list */}
        <div className="mpage__mob-sheet">

          {/* ── Mobile: Sector family list (when 1 sector selected) ── */}
          {showFamilyPanel && selectedSectorObj ? (
            <div className="mpage__mob-families">
              <div className="mpage__mob-families-header">
                <h3>Sector {selectedSectorObj.number}</h3>
                <span>{sectorFamilies.length} familii</span>
                <button onClick={() => { setSelectedSectors(new Set()); setMobSheetOpen(false); }}>
                  <IonIcon icon={close} />
                </button>
              </div>
              {pastoralOnly ? (
                <div className="mpage__mob-families-badge">Doar vizite pastorale</div>
              ) : null}
              <div className="mpage__mob-families-search">
                <IonIcon icon={searchOutline} style={{ fontSize: 16, color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Caută familie..."
                  value={familySearch}
                  onChange={(e) => setFamilySearch(e.target.value)}
                />
                {familySearch ? (
                  <button onClick={() => setFamilySearch('')}><IonIcon icon={close} /></button>
                ) : null}
              </div>
              <div className="mpage__mob-families-list">
                {(() => {
                  const q = familySearch.trim().toLowerCase();
                  const statusOrder: Record<string, number> = { gray: 0, red: 1, yellow: 2, green: 3 };
                  let list = [...sectorFamilies];
                  if (q.length >= 2) {
                    list = list.filter(f => {
                      const name = (f.display_name ?? f.name).toLowerCase();
                      const addr = (f.address ?? '').toLowerCase();
                      return name.includes(q) || addr.includes(q);
                    });
                  }
                  list.sort((a, b) => {
                    const sa = pastoralOnly ? (a.pastoral_visit_status || 'gray') : a.visit_status;
                    const sb = pastoralOnly ? (b.pastoral_visit_status || 'gray') : b.visit_status;
                    return (statusOrder[sa] ?? 0) - (statusOrder[sb] ?? 0);
                  });
                  if (list.length === 0) {
                    return <div className="mpage__mob-families-empty">{q ? `Nicio familie pentru „${q}"` : 'Nicio familie'}</div>;
                  }
                  return list.map(family => {
                    const status = pastoralOnly ? (family.pastoral_visit_status || 'gray') : family.visit_status;
                    const { color } = STATUS_CONFIG[status];
                    const hasLoc = !!family.latitude && !!family.longitude;
                    const date = pastoralOnly ? family.last_pastoral_visit_date : family.last_visit_date;
                    const dateStr = date ? new Date(date).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' }) : null;
                    return (
                      <button
                        key={family.id}
                        className="mpage__mob-family-row"
                        onClick={() => { if (hasLoc) { setFlyTarget(family); setMobSheetOpen(false); } }}
                        disabled={!hasLoc}
                      >
                        <span className="mpage__mob-family-dot" style={{ background: color }} />
                        <div className="mpage__mob-family-info">
                          <span className="mpage__mob-family-name">{family.display_name ?? family.name}</span>
                          <span className="mpage__mob-family-meta" style={{ color }}>
                            {STATUS_CONFIG[status].label}
                            {dateStr ? ` · ${dateStr}` : ''}
                            {family.last_visit_type ? ` · ${VISIT_TYPE_LABELS[family.last_visit_type as VisitType] ?? ''}` : ''}
                          </span>
                        </div>
                        <div className="mpage__mob-family-actions">
                          <button
                            className="mpage__mob-family-btn"
                            onClick={(e) => { e.stopPropagation(); setVisitFamilyId(family.id); }}
                          >
                            <IonIcon icon={addOutline} /> Vizită
                          </button>
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          ) : (
            <>

          {/* Status filter row */}
          <div className="mpage__mob-section">
            <div className="mpage__mob-statuses">
              {(Object.keys(STATUS_CONFIG) as VisitStatus[]).map(status => {
                const { color, label } = STATUS_CONFIG[status];
                const count = stats[status];
                const isActive = selectedStatuses.has(status);
                return (
                  <button
                    key={status}
                    className={`mpage__mob-status${isActive ? ' mpage__mob-status--active' : ''}`}
                    onClick={() => toggleStatus(status)}
                    style={isActive ? { borderColor: color, background: `${color}12` } : undefined}
                  >
                    <span className="mpage__mob-status-dot" style={{ background: color }} />
                    <span className="mpage__mob-status-label">{label}</span>
                    <span className="mpage__mob-status-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sector chips */}
          <div className="mpage__mob-section">
            <div className="mpage__mob-chips">
              {sectors.map(sector => {
                const isActive = selectedSectors.has(sector.id);
                return (
                  <button
                    key={sector.id}
                    className={`mpage__mob-chip${isActive ? ' mpage__mob-chip--active' : ''}`}
                    onClick={() => toggleSector(sector.id)}
                    style={isActive ? { background: sector.color || '#5e81f4', borderColor: sector.color || '#5e81f4' } : undefined}
                  >
                    {!isActive && sector.color && (
                      <span className="mpage__mob-chip-dot" style={{ background: sector.color }} />
                    )}
                    S{sector.number}
                    {isActive && <IonIcon icon={close} style={{ fontSize: 11 }} />}
                  </button>
                );
              })}
              {/* Boundaries toggle inline */}
              <button
                className={`mpage__mob-chip mpage__mob-chip--toggle${showBoundaries ? ' mpage__mob-chip--active' : ''}`}
                onClick={() => setShowBoundaries(!showBoundaries)}
                style={showBoundaries ? { background: '#475569', borderColor: '#475569' } : undefined}
              >
                <IonIcon icon={layersOutline} style={{ fontSize: 13 }} />
                Limite
              </button>
            </div>
          </div>

          {/* Ajutoare filter (mobile) */}
          <div className="mpage__mob-section">
            <div className="mpage__mob-chips">
              {([['received', 'Au primit'], ['not_received', 'Nu au primit']] as const).map(([key, label]) => {
                const isActive = ajutoareFilter === key;
                return (
                  <button
                    key={key}
                    className={`mpage__mob-chip${isActive ? ' mpage__mob-chip--active' : ''}`}
                    onClick={() => setAjutoareFilter(isActive ? null : key)}
                    style={isActive ? { background: '#d97706', borderColor: '#d97706' } : undefined}
                  >
                    <IonIcon icon={giftOutline} style={{ fontSize: 13 }} />
                    {label}
                    {isActive && <IonIcon icon={close} style={{ fontSize: 11 }} />}
                  </button>
                );
              })}
              {ajutoareFilter && ([3, 6, 12] as const).map(months => {
                const isActive = ajutoareMonths === months;
                return (
                  <button
                    key={months}
                    className={`mpage__mob-chip${isActive ? ' mpage__mob-chip--active' : ''}`}
                    onClick={() => setAjutoareMonths(months)}
                    style={isActive ? { background: '#92400e', borderColor: '#92400e' } : undefined}
                  >
                    {months}l
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search */}
          <div className="mpage__mob-section">
            <div className="mside__search-wrap">
              <IonIcon icon={searchOutline} className="mside__search-icon" />
              <input
                type="text"
                className="mside__search-input"
                placeholder="Caută familie..."
                value={familySearch}
                onChange={e => setFamilySearch(e.target.value)}
              />
              {familySearch && (
                <button className="mside__search-clear" onClick={() => setFamilySearch('')}>
                  <IonIcon icon={close} />
                </button>
              )}
            </div>
            {familySearch.trim().length >= 2 && (
              <div className="mside__search-results" style={{ maxHeight: 160 }}>
                {familySearchResults.length === 0 ? (
                  <div className="mside__search-empty">Nicio familie găsită</div>
                ) : (
                  familySearchResults.map(family => (
                    <button
                      key={family.id}
                      className="mside__search-item"
                      onClick={() => {
                        if (family.latitude && family.longitude) {
                          setFlyTarget(family);
                          setFamilySearch('');
                          setMobSheetOpen(false);
                        }
                      }}
                      disabled={!family.latitude || !family.longitude}
                    >
                      <span className="mside__search-dot" style={{ background: STATUS_CONFIG[family.visit_status].color }} />
                      <div className="mside__search-info">
                        <span className="mside__search-name">{family.display_name ?? family.name}</span>
                        {family.address && <span className="mside__search-addr">{family.address}</span>}
                      </div>
                      <IonIcon icon={navigateOutline} className="mside__search-fly" style={{ opacity: 1 }} />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div className="mpage__mob-actions">
            <button
              className="mpage__mob-action mpage__mob-action--route"
              onClick={() => { history.push(`/maps/route-planner${urlSectorId ? `?sector_id=${urlSectorId}` : ''}`); }}
            >
              <IonIcon icon={navigateOutline} />
              Planifică vizite
            </button>
            <button
              className="mpage__mob-action mpage__mob-action--ajutoare"
              onClick={() => { history.push(`/maps/route-planner?mode=ajutoare${urlSectorId ? `&sector_id=${urlSectorId}` : ''}`); setMobSheetOpen(false); }}
            >
              <IonIcon icon={giftOutline} />
              Distribuie ajutoare
            </button>
            {activeFilterCount > 0 && (
              <button className="mpage__mob-action mpage__mob-action--clear" onClick={clearAllFilters}>
                <IonIcon icon={close} />
                Șterge filtre
              </button>
            )}
          </div>

            </>
          )}
        </div>
      </div>

      {/* ── Floating stats cards (top-right) ── */}
      <div className="mpage__stats">
        <div className="mstat">
          <span className="mstat__value">{stats.total}</span>
          <span className="mstat__label">Familii</span>
        </div>
        <div className="mstat mstat--green">
          <span className="mstat__value">{stats.green + stats.yellow}</span>
          <span className="mstat__label">Sub 6 luni</span>
        </div>
        <div className="mstat mstat--red">
          <span className="mstat__value">{stats.red + stats.gray}</span>
          <span className="mstat__label">Peste 12 luni</span>
        </div>
      </div>

      {/* ── Floating action buttons ── */}
      <div className="mpage__float-actions">
        <button
          className="mpage__route-btn"
          onClick={() => history.push(`/maps/route-planner${urlSectorId ? `?sector_id=${urlSectorId}` : ''}`)}
        >
          <IonIcon icon={navigateOutline} />
          <span>Planifică vizite</span>
        </button>

        <button
          className="mpage__ajutoare-btn"
          onClick={() => history.push(`/maps/route-planner?mode=ajutoare${urlSectorId ? `&sector_id=${urlSectorId}` : ''}`)}
        >
          <IonIcon icon={giftOutline} />
          <span>Distribuie ajutoare</span>
        </button>

        {pendingCount > 0 && (
          <button className="mpage__review-btn" onClick={() => history.push(`/maps/review${urlSectorId ? `?sector_id=${urlSectorId}` : ''}`)}>
            <IonIcon icon={locationOutline} />
            <span>{pendingCount} de verificat</span>
          </button>
        )}
      </div>

      {/* ── Sector Family Panel (right split) ── */}
      {showFamilyPanel && selectedSectorObj ? (
        <SectorFamilyPanel
          families={sectorFamilies}
          sectorName={`Sector ${selectedSectorObj.number ?? selectedSectorObj.name}`}
          pastoralOnly={pastoralOnly}
          onClose={() => setSelectedSectors(new Set())}
          onFlyTo={(family) => setFlyTarget(family)}
          onAddVisit={(familyId) => setVisitFamilyId(familyId)}
        />
      ) : null}

      {/* ── Visit/Need modals (from map popup) ── */}
      <VisitModal
        isOpen={!!visitFamilyId}
        onClose={() => setVisitFamilyId(null)}
        familyId={visitFamilyId ?? undefined}
        onSuccess={() => { void refetchMap(); }}
      />
      <VisitModal
        isOpen={!!ajutorFamilyId}
        onClose={() => setAjutorFamilyId(null)}
        familyId={ajutorFamilyId ?? undefined}
        onSuccess={() => { void refetchMap(); }}
        defaultVisitType="ajutoare"
      />
      <NeedModal
        isOpen={!!needFamilyId}
        onClose={() => setNeedFamilyId(null)}
        familyId={needFamilyId ?? undefined}
      />
    </div>
  );
};

export default MapPage;
